// ============================================
// Housing Assessment Section
// CS_Assessment - Housing Navigation & Housing Sustaining (CalAIM)
// Matches PDF fields exactly
// ============================================

import React, { useRef, useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { FormSubmission } from '../../types';
import FormField from '../ui/FormField';
import { useLanguage } from '../../i18n/LanguageContext';

const HousingSection: React.FC = () => {
  const { register, watch, control, setValue, formState: { errors } } = useFormContext<FormSubmission>();
  const { t } = useLanguage();

  // Housing situation options with translations
  const HOUSING_SITUATIONS = [
    { value: 'homeless_unsheltered', label: t('housing.unsheltered'), description: t('housing.unshelteredDesc') },
    { value: 'homeless_sheltered', label: t('housing.emergencyShelter'), description: '' },
    { value: 'doubled_up', label: t('housing.doubledUp'), description: '' },
    { value: 'imminent_risk', label: t('housing.imminentRisk'), description: t('housing.imminentRiskDesc') },
    { value: 'at_risk_eviction', label: t('housing.evictionRisk'), description: '' },
    { value: 'recently_housed', label: t('housing.recentlyHoused'), description: t('housing.recentlyHousedDesc') },
  ];

  // Assessor signature refs and state
  const assessorSigRef = useRef<SignatureCanvas>(null);
  const [assessorSigType, setAssessorSigType] = useState<'draw' | 'text' | 'upload'>('draw');
  const [assessorTextSig, setAssessorTextSig] = useState('');

  // Watch for conditional fields
  const barrierOther = watch('housing.barriers.other');
  const referralOther = watch('housing.referralSource.other');
  const unitOther = watch('housing.preferences.unitOther');
  const accessibilityOther = watch('housing.preferences.accessibilityOther');
  const incomeOther = watch('housing.income.other');
  const safetyConcernsYes = watch('housing.safety.safetyConcernsYes');
  const evictionRisk30Yes = watch('housing.safety.evictionRisk30Yes');
  const repeatedLossYes = watch('housing.safety.repeatedLossYes');
  const assessorSignature = watch('housing.assessorSignature');

  // For diagnoses array
  const { fields: diagnosisFields, append: appendDiagnosis, remove: removeDiagnosis } = useFieldArray({
    control,
    name: 'housing.diagnoses' as never,
  });

  // Assessor signature handlers
  const handleAssessorSignatureEnd = () => {
    if (assessorSigRef.current && !assessorSigRef.current.isEmpty()) {
      const dataUrl = assessorSigRef.current.getTrimmedCanvas().toDataURL('image/png');
      setValue('housing.assessorSignature', dataUrl);
    }
  };

  const clearAssessorSignature = () => {
    if (assessorSigRef.current) {
      assessorSigRef.current.clear();
    }
    setValue('housing.assessorSignature', '');
    setAssessorTextSig('');
  };

  const generateAssessorTextSignature = (text: string) => {
    if (!text.trim()) {
      setValue('housing.assessorSignature', '');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Completely transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#000000';
      ctx.font = 'italic 72px "Brush Script MT", cursive, serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, canvas.width / 2, canvas.height / 2);
      const dataUrl = canvas.toDataURL('image/png');
      setValue('housing.assessorSignature', dataUrl);
    }
  };

  const handleAssessorFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert(t('housing.uploadImageFile'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(t('housing.fileSizeLimit'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setValue('housing.assessorSignature', dataUrl);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-8">
      <div className="bg-blue-50 p-4 rounded-lg">
        <h2 className="text-lg font-semibold text-blue-900">{t('housing.title')}</h2>
        <p className="text-sm text-blue-700">{t('housing.subtitle')}</p>
      </div>

      {/* Referral Source */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('housing.referralSource')}</h3>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center space-x-2 p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100">
            <input type="checkbox" {...register('housing.referralSource.mcp')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.mcp')}</span>
          </label>
          <label className="flex items-center space-x-2 p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100">
            <input type="checkbox" {...register('housing.referralSource.ecm')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.ecm')}</span>
          </label>
          <label className="flex items-center space-x-2 p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100">
            <input type="checkbox" {...register('housing.referralSource.provider')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.provider')}</span>
          </label>
          <label className="flex items-center space-x-2 p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100">
            <input type="checkbox" {...register('housing.referralSource.self')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.self')}</span>
          </label>
          <label className="flex items-center space-x-2 p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100">
            <input type="checkbox" {...register('housing.referralSource.other')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.other')}</span>
          </label>
        </div>
        {referralOther && (
          <div className="mt-2">
            <input
              type="text"
              {...register('housing.referralSource.otherDescription')}
              className="form-input w-full max-w-xs rounded-md border-gray-300"
              placeholder={t('housing.specifyOther')}
            />
          </div>
        )}
      </div>

      {/* Managed Care Plan */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label={t('housing.managedCarePlan')}>
          <input
            type="text"
            {...register('housing.managedCarePlan')}
            className="form-input w-full rounded-md border-gray-300"
            placeholder={t('housing.managedCarePlanPlaceholder')}
          />
        </FormField>
        <FormField label={t('housing.assessmentDate')}>
          <input
            type="date"
            min="1900-01-01"
            {...register('housing.assessmentDate', )}
            className="form-input w-full rounded-md border-gray-300"
          />
        </FormField>
      </div>

      {/* A. Housing Status (Required) */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('housing.housingStatus')}
          
        </h3>
        <p className="text-sm text-gray-500 mb-4">{t('housing.currentSituation')}</p>

        <div className="space-y-3">
          {HOUSING_SITUATIONS.map((situation) => (
            <label
              key={situation.value}
              className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
            >
              <input
                type="radio"
                {...register('housing.currentSituation', )}
                value={situation.value}
                className="form-radio h-5 w-5 text-blue-600 mt-0.5"
              />
              <div>
                <span className="text-gray-900 font-medium">{situation.label}</span>
                {situation.description && <p className="text-sm text-gray-500">{situation.description}</p>}
              </div>
            </label>
          ))}
        </div>
        {errors.housing?.currentSituation && (
          <p className="mt-2 text-sm text-red-600">{errors.housing.currentSituation.message}</p>
        )}

        <div className="mt-4">
          <FormField label={t('housing.lengthSituationLabel')}>
            <input
              type="text"
              {...register('housing.lengthOfSituation')}
              className="form-input w-full max-w-xs rounded-md border-gray-300"
              placeholder={t('housing.lengthPlaceholder')}
            />
          </FormField>
        </div>
      </div>

      {/* Primary Barriers to Housing */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('housing.primaryBarriers')}</h3>
        <p className="text-sm text-gray-500 mb-4">{t('housing.checkAllApply')}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.barriers.lowIncome')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.lowIncome')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.barriers.poorCredit')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.poorCredit')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.barriers.priorEviction')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.priorEviction')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.barriers.criminalHistory')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.criminalHistory')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.barriers.disability')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.disability')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.barriers.behavioralHealth')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.behavioralHealth')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.barriers.lackDocuments')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.lackDocuments')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.barriers.languageBarriers')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.languageBarriers')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.barriers.transportationBarriers')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.transportation')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.barriers.other')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.other')}</span>
          </label>
        </div>

        {barrierOther && (
          <div className="mt-4">
            <input
              type="text"
              {...register('housing.barriers.otherDescription')}
              className="form-input w-full rounded-md border-gray-300"
              placeholder={t('housing.describeOtherBarriers')}
            />
          </div>
        )}
      </div>

      {/* B. Eligibility & Medical Appropriateness */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('housing.eligibilityMedical')}</h3>

        <div className="space-y-4">
          <FormField label={t('housing.healthRelatedNeeds')}>
            <textarea
              {...register('housing.healthRelatedNeeds')}
              rows={3}
              className="form-textarea w-full rounded-md border-gray-300"
              placeholder={t('housing.healthRelatedNeedsPlaceholder')}
            />
          </FormField>

          <FormField label={t('housing.relevantDiagnoses')}>
            <textarea
              {...register('housing.relevantDiagnoses')}
              rows={2}
              className="form-textarea w-full rounded-md border-gray-300"
              placeholder={t('housing.relevantDiagnosesPlaceholder')}
            />
          </FormField>

          <FormField label={t('housing.howHousingHelps')}>
            <textarea
              {...register('housing.howHousingHelps')}
              rows={3}
              className="form-textarea w-full rounded-md border-gray-300"
              placeholder={t('housing.howHousingHelpsPlaceholder')}
            />
          </FormField>
        </div>
      </div>

      {/* C. Housing Navigation Assessment */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('housing.navigationAssessment')}</h3>
        <p className="text-sm text-gray-500 mb-4">{t('housing.navCompleteNote')}</p>

        <p className="text-sm font-medium text-gray-700 mb-3">{t('housing.navAssistanceLabel')}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.navigation.housingSearch')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.navHousingSearch')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.navigation.applications')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.navApplications')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.navigation.documents')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.navDocuments')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.navigation.accommodation')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.navAccommodation')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.navigation.landlordEngagement')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.navLandlord')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.navigation.leaseTerms')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.navLeaseTerms')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.navigation.moveIn')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.navMoveIn')}</span>
          </label>
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <input type="checkbox" {...register('housing.navigation.rentalAssistance')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
            <span>{t('housing.navRentalAssistance')}</span>
          </label>
        </div>

        {/* Housing Preferences */}
        <div className="mt-6 space-y-4">
          <h4 className="font-medium text-gray-800">{t('housing.preferencesHeading')}</h4>

          <FormField label={t('housing.locationCity')}>
            <input
              type="text"
              {...register('housing.preferences.locationCity')}
              className="form-input w-full max-w-md rounded-md border-gray-300"
              placeholder={t('housing.locationCityPlaceholder')}
            />
          </FormField>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">{t('housing.unitType')}</p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.preferences.unitStudio')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.unitStudio')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.preferences.unit1BR')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.unit1BR')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.preferences.unit2BR')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.unit2BR')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.preferences.unitOther')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.unitOther')}</span>
              </label>
            </div>
            {unitOther && (
              <input
                type="text"
                {...register('housing.preferences.unitOtherDescription')}
                className="form-input mt-2 w-full max-w-xs rounded-md border-gray-300"
                placeholder={t('housing.specifyOther')}
              />
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">{t('housing.accessibilityNeeds')}</p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.preferences.accessibilityNone')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.accessibilityNone')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.preferences.accessibilityADA')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.accessibilityADA')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.preferences.accessibilityOther')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.accessibilityOther')}</span>
              </label>
            </div>
            {accessibilityOther && (
              <input
                type="text"
                {...register('housing.preferences.accessibilityOtherDescription')}
                className="form-input mt-2 w-full max-w-xs rounded-md border-gray-300"
                placeholder={t('housing.specifyOther')}
              />
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">{t('housing.incomeSources')}</p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.income.ssiSsdi')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.incomeSsiSsdi')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.income.employment')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.incomeEmployment')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.income.ga')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.incomeGa')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.income.none')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.incomeNone')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" {...register('housing.income.other')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                <span>{t('housing.incomeOther')}</span>
              </label>
            </div>
            {incomeOther && (
              <input
                type="text"
                {...register('housing.income.otherDescription')}
                className="form-input mt-2 w-full max-w-xs rounded-md border-gray-300"
                placeholder={t('housing.specifyOther')}
              />
            )}
          </div>
        </div>
      </div>

      {/* D. Housing Sustaining Assessment */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('housing.sustaingingAssessment')}</h3>
        <p className="text-sm text-gray-500 mb-4">{t('housing.sustainCompleteNote')}</p>

        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">{t('housing.leaseStatus')}</p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center space-x-2">
              <input type="radio" {...register('housing.sustaining.newLease')} value="true" className="form-radio h-5 w-5 text-blue-600" />
              <span>{t('housing.newLease')}</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="radio" {...register('housing.sustaining.existingLease')} value="true" className="form-radio h-5 w-5 text-blue-600" />
              <span>{t('housing.existingLease')}</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="radio" {...register('housing.sustaining.informal')} value="true" className="form-radio h-5 w-5 text-blue-600" />
              <span>{t('housing.informalArrangement')}</span>
            </label>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-3">{t('housing.riskFactorsLabel')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.riskFactors.rentUtilities')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.riskRentUtilities')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.riskFactors.leaseViolations')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.riskLeaseViolations')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.riskFactors.conflict')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.riskConflict')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.riskFactors.maintainingUnit')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.riskMaintainingUnit')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.riskFactors.missedAppointments')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.miscAppointments')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.riskFactors.behavioralSymptoms')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.behavioralSymptoms')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.riskFactors.lackTenancySkills')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.lackTenancySkills')}</span>
            </label>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 mb-3">{t('housing.supportNeedsLabel')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.supportNeeds.tenantRights')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.tenantRights')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.supportNeeds.budgeting')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.budgeting')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.supportNeeds.utilitySetup')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.utilitySetup')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.supportNeeds.landlordComm')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.landlordComm')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.supportNeeds.crisisPrevention')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.crisisPrevention')}</span>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.supportNeeds.ongoingServices')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.ongoingServices')}</span>
            </label>
          </div>
        </div>
      </div>

      {/* E. Risk & Safety Screening */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('housing.riskSafety')}</h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">{t('housing.safetyConcerns')}</span>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2">
                <input type="radio" {...register('housing.safety.safetyConcernsYes')} value="true" className="form-radio h-5 w-5 text-blue-600" />
                <span>{t('housing.yes')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" {...register('housing.safety.safetyConcernsNo')} value="true" className="form-radio h-5 w-5 text-blue-600" />
                <span>{t('housing.no')}</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">{t('housing.evictionRisk30')}</span>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2">
                <input type="radio" {...register('housing.safety.evictionRisk30Yes')} value="true" className="form-radio h-5 w-5 text-blue-600" />
                <span>{t('housing.yes')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" {...register('housing.safety.evictionRisk30No')} value="true" className="form-radio h-5 w-5 text-blue-600" />
                <span>{t('housing.no')}</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">{t('housing.repeatedLoss')}</span>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2">
                <input type="radio" {...register('housing.safety.repeatedLossYes')} value="true" className="form-radio h-5 w-5 text-blue-600" />
                <span>{t('housing.yes')}</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" {...register('housing.safety.repeatedLossNo')} value="true" className="form-radio h-5 w-5 text-blue-600" />
                <span>{t('housing.no')}</span>
              </label>
            </div>
          </div>

          {(safetyConcernsYes || evictionRisk30Yes || repeatedLossYes) && (
            <FormField label={t('housing.mitigationPlan')}>
              <textarea
                {...register('housing.safety.mitigationPlan')}
                rows={3}
                className="form-textarea w-full rounded-md border-gray-300"
                placeholder={t('housing.mitigationPlanPlaceholder')}
              />
            </FormField>
          )}
        </div>
      </div>

      {/* F. Member Goals */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('housing.memberGoals')}</h3>
        <FormField label={t('housing.memberGoalsLabel')}>
          <textarea
            {...register('housing.memberGoals')}
            rows={4}
            className="form-textarea w-full rounded-md border-gray-300"
            placeholder={t('housing.memberGoalsPlaceholder')}
          />
        </FormField>
      </div>

      {/* G. Service Recommendation */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('housing.serviceRecommendation')}</h3>

        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-3">{t('housing.recommendedSupports')}</p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.recommendation.housingNavigation')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.housingNavigation')}</span>
            </label>
            <label className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.recommendation.housingSustaining')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.housingSustaining')}</span>
            </label>
            <label className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" {...register('housing.recommendation.both')} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
              <span>{t('housing.both')}</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <FormField label={t('housing.durationFrequency')}>
            <input
              type="text"
              {...register('housing.recommendation.durationFrequency')}
              className="form-input w-full rounded-md border-gray-300"
              placeholder={t('housing.durationFrequencyPlaceholder')}
            />
          </FormField>

          <FormField label={t('housing.rationale')}>
            <textarea
              {...register('housing.recommendation.rationale')}
              rows={3}
              className="form-textarea w-full rounded-md border-gray-300"
              placeholder={t('housing.rationalePlaceholder')}
            />
          </FormField>
        </div>
      </div>

      {/* Assessor Information */}
      <div className="border-t pt-6 bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('housing.assessorInfo')}</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <FormField label={t('housing.assessorName')}>
            <input
              type="text"
              {...register('housing.assessorName', )}
              className="form-input w-full rounded-md border-gray-300"
              placeholder={t('housing.assessorNamePlaceholder')}
            />
          </FormField>

          <FormField label={t('housing.assessorTitle')}>
            <input
              type="text"
              {...register('housing.assessorTitle')}
              className="form-input w-full rounded-md border-gray-300"
              placeholder={t('housing.titlePlaceholder')}
            />
          </FormField>

          <FormField label={t('housing.assessorSignatureDate')}>
            <input
              type="date"
              min="1900-01-01"
              {...register('housing.assessorSignatureDate')}
              className="form-input w-full rounded-md border-gray-300"
            />
          </FormField>
        </div>

        {/* Assessor Signature */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('housing.assessorSignature')} 
          </label>

          {/* Signature Type Selector */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => { setAssessorSigType('draw'); clearAssessorSignature(); }}
              className={`px-3 py-1.5 text-sm rounded-md border ${assessorSigType === 'draw' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              {t('housing.draw')}
            </button>
            <button
              type="button"
              onClick={() => { setAssessorSigType('text'); clearAssessorSignature(); }}
              className={`px-3 py-1.5 text-sm rounded-md border ${assessorSigType === 'text' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              {t('housing.type')}
            </button>
            <button
              type="button"
              onClick={() => { setAssessorSigType('upload'); clearAssessorSignature(); }}
              className={`px-3 py-1.5 text-sm rounded-md border ${assessorSigType === 'upload' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              {t('housing.upload')}
            </button>
          </div>

          {/* Draw Signature */}
          {assessorSigType === 'draw' && (
            <div>
              <div className="border-2 border-gray-300 rounded-lg bg-transparent" style={{ width: '100%', maxWidth: '800px' }}>
                <SignatureCanvas
                  ref={assessorSigRef}
                  penColor="black"
                  minWidth={2}
                  maxWidth={4}
                  canvasProps={{
                    width: 800,
                    height: 300,
                    className: 'signature-canvas rounded-lg',
                    style: { width: '100%', height: '300px', backgroundColor: 'transparent' }
                  }}
                  onEnd={handleAssessorSignatureEnd}
                />
              </div>
              <button
                type="button"
                onClick={clearAssessorSignature}
                className="mt-2 text-sm text-red-600 hover:text-red-800"
              >
                {t('housing.clearSignature')}
              </button>
            </div>
          )}

          {/* Type Signature */}
          {assessorSigType === 'text' && (
            <div>
              <input
                type="text"
                value={assessorTextSig}
                onChange={(e) => {
                  setAssessorTextSig(e.target.value);
                  generateAssessorTextSignature(e.target.value);
                }}
                className="form-input w-full max-w-md rounded-md border-gray-300"
                placeholder={t('housing.typeFullName')}
              />
              {assessorSignature && (
                <div className="mt-2 p-2 bg-white border rounded-lg" style={{ maxWidth: '400px' }}>
                  <img src={assessorSignature} alt={t('housing.signaturePreview')} className="max-h-16" />
                </div>
              )}
            </div>
          )}

          {/* Upload Signature */}
          {assessorSigType === 'upload' && (
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleAssessorFileUpload}
                className="form-input w-full max-w-md rounded-md border-gray-300"
              />
              {assessorSignature && (
                <div className="mt-2 p-2 bg-white border rounded-lg" style={{ maxWidth: '400px' }}>
                  <img src={assessorSignature} alt={t('housing.uploadedSignature')} className="max-h-20" />
                  <button
                    type="button"
                    onClick={clearAssessorSignature}
                    className="mt-1 text-sm text-red-600 hover:text-red-800"
                  >
                    {t('housing.remove')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Show captured signature */}
          {assessorSigType === 'draw' && assessorSignature && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg" style={{ maxWidth: '400px' }}>
              <p className="text-xs text-green-700 mb-1">{t('housing.signatureCaptured')}</p>
              <img src={assessorSignature} alt={t('housing.assessorSignature')} className="max-h-16" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HousingSection;



